import { z } from 'zod';
import { deviceTypeNameEnum } from '@/features/devices/schemas/devices.schemas.ts';
import { conditionSchema } from '@/features/rules-shared/schemas/condition-rules.utils.ts';

const RULE_TYPES = ['plant', 'device', 'schedule'] as const;
const ruleTypeEnum = z.enum(RULE_TYPES);
export type NotificationRuleTypes = (typeof RULE_TYPES)[number];

const NOTIFICATION_RULES_STATUSES = ['disabled', 'enabled'] as const;
const notificationRuleStatusesEnum = z.enum(NOTIFICATION_RULES_STATUSES);
export type NotificationRuleStatuses = (typeof NOTIFICATION_RULES_STATUSES)[number];

const MANAGED_BY_MODES = ['self-managed', 'company-managed'] as const;
const managedByEnum = z.enum(MANAGED_BY_MODES);
export type ManagedByMode = (typeof MANAGED_BY_MODES)[number];

export const notificationRuleBaseSchema = z.object({
  status: notificationRuleStatusesEnum.default('enabled'),

  notificationRuleId: z.string({ error: 'Notification rule id required' }),

  companyId: z.string({
    error: 'Company ID is required',
  }),
  plantId: z.string({ error: 'Plant ID is required' }).min(1, 'Plant ID is required'),
  userId: z.string().nullable(),

  plantName: z.string({ error: 'Plant name is required' }).min(1, 'Plant name is required'),

  deviceId: z.string().optional(),
  deviceType: deviceTypeNameEnum.optional(),
  deviceSN: z.string().optional(),

  notificationTitle: z
    .string({ error: 'Notification title is required' })
    .min(3, 'Notification title must be at least 3 characters')
    .max(30, 'Notification title must be less than 30 characters'),

  notificationTemplate: z
    .string({ error: 'Notification template is required' })
    .min(10, 'Notification template must be at least 10 characters')
    .max(160, 'Notification template must be less than 160 characters'),
  notificationPreviewTemplate: z
    .string({ error: 'Notification preview template is required' })
    .min(10, 'Notification preview template must be at least 10 characters')
    .max(160, 'Notification preview template must be less than 160 characters'),

  ruleType: ruleTypeEnum,

  conditions: z.array(conditionSchema).min(1, 'At least one condition is required'),

  cooldownPeriod: z
    .number({ error: 'Cooldown period is required' })
    .min(60, 'Cooldown period must be at least 60 minutes')
    .max(10080, 'Cooldown period cannot exceed 7 days (10080 minutes)')
    .default(60),

  managedBy: managedByEnum.default('self-managed'),
  createdBy: z.string().optional().nullable(),
  updatedBy: z.string().optional().nullable(),

  forwardedFrom: z.string().optional(),
  forwardedNotificationRuleId: z.string().optional().nullable(),

  lastTriggeredAt: z.string().optional().nullable(),
  triggerCount: z.number().optional().nullable(),

  updatedAt: z.string().optional(),
  createdAt: z.string().optional(),
});

export type NotificationRule = z.infer<typeof notificationRuleBaseSchema>;

export const notificationRuleStatuses = [
  {
    value: 'enabled',
    label: 'Enabled',
    icon: undefined,
  },
  {
    value: 'disabled',
    label: 'Disabled',
    icon: undefined,
  },
];
